import { Component, Input, forwardRef, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { FileUploadService } from '../../../services/file-upload.service';
import { GuidService } from '../../../services/guid.service';
import { I18nService } from '../../../services/i18n.service';
import { FileItem, FileUploadValue } from './models/file-item.interface';
import { FileUploadConfig } from './models/file-upload-config.interface';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-file-upload-manager',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, TranslatePipe],
  template: `
    <div class="file-upload-manager-container">
      <label [for]="id" [class.required-field]="required">
        {{ label | translate }}
      </label>

      <!-- 範例檔案列表 -->
      <div class="example-files" *ngIf="exampleFiles.length > 0">
        <!-- <h5 class="file-section-title">{{ 'file_upload.example_files' | translate }}</h5> -->
        <div class="file-list">
          <div class="example-file-item" *ngFor="let file of exampleFiles; let i = index">
            <!-- 檔案資訊 -->
            <div class="file-info">
              <div class="file-name-container">
                <!-- <i [class]="getFileIcon(file.type)"></i> -->
                <span class="file-name">
                  {{ file.name }}
                </span>
              </div>
              <!-- <div class="file-meta" *ngIf="config.showFileSize || config.showUploadTime">
                <span class="file-size" *ngIf="config.showFileSize">
                  {{ formatFileSize(file.size) }}
                </span>
                <span class="upload-time" *ngIf="config.showUploadTime && file.uploadedAt">
                  {{ formatUploadTime(file.uploadedAt) }}
                </span>
              </div> -->
            </div>

            <!-- 下載範本按鈕 -->
            <div class="example-file-actions">
              <button type="button"
                      class="btn btn-primary btn-sm"
                      (click)="viewFile(file)"
                      [title]="'file_upload.download_template' | translate">
                <i class="fas fa-download"></i>
                {{ 'file_upload.download_template' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 檔案上傳區域（預覽模式或允許用戶上傳時顯示） -->
      <div class="file-upload-area"
           *ngIf="(preview || config.allowUserUpload) && canUploadMore()"
           [class.drag-over]="isDragOver"
           [class.disabled]="disabled"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave($event)"
           (drop)="onFileDrop($event)"
           (click)="triggerFileSelect()">
        <div class="upload-content">
          <i class="fas fa-cloud-upload-alt upload-icon"></i>
          <p class="upload-text">
            {{ config.dragDropText || 'file_upload.drag_drop_text' | translate }}
          </p>
          <p class="upload-hint">
            {{ getUploadHint() }}
          </p>
        </div>
        <input #fileInput
               type="file"
               [id]="id"
               [name]="name"
               (change)="onFileSelected($event)"
               [accept]="getAcceptedTypes()"
               [multiple]="config.maxFiles !== 1"
               hidden>
      </div>

      <!-- 使用者上傳的檔案列表 -->
      <div class="user-uploaded-files" *ngIf="userUploadedFiles.length > 0">
        <h5 class="file-section-title">{{ 'file_upload.uploaded_files' | translate }}</h5>
        <div class="file-list">
          <div class="file-item user-file" *ngFor="let file of userUploadedFiles; let i = index"
               [class.uploading]="file.uploading"
               [class.error]="file.error">
            <!-- 檔案資訊 -->
            <div class="file-info">
              <div class="file-name-container">
                <i [class]="getFileIcon(file.type)"></i>
                <span class="file-name"
                      *ngIf="!file.uploading && !file.error"
                      [class.clickable]="true"
                      (click)="viewFile(file)">
                  {{ file.name }}
                </span>
                <span class="file-name" *ngIf="file.uploading || file.error">
                  {{ file.name }}
                </span>
              </div>
              <div class="file-meta" *ngIf="config.showFileSize || config.showUploadTime">
                <span class="file-size" *ngIf="config.showFileSize">
                  {{ formatFileSize(file.size) }}
                </span>
                <span class="upload-time" *ngIf="config.showUploadTime && file.uploadedAt">
                  {{ formatUploadTime(file.uploadedAt) }}
                </span>
              </div>
            </div>

            <!-- 上傳進度（僅編輯模式顯示） -->
            <!-- <div class="upload-progress" *ngIf="preview && file.uploading">
              <div class="progress-bar">
                <div class="progress-fill" [style.width.%]="file.uploadProgress || 0"></div>
              </div>
              <span class="progress-text">{{ file.uploadProgress || 0 }}%</span>
            </div> -->

            <!-- 錯誤訊息（僅編輯模式顯示） -->
            <div class="error-message" *ngIf="preview && file.error">
              <i class="fas fa-exclamation-triangle"></i>
              {{ file.error }}
            </div>

            <!-- 操作按鈕 -->
            <div class="file-actions" *ngIf="!file.uploading">
              <!-- 查看按鈕 -->
              <!-- <button type="button"
                      class="btn btn-primary btn-sm"
                      *ngIf="file.viewUrl && !file.error"
                      (click)="viewFile(file)"
                      [title]="'file_upload.view_file' | translate">
                <i class="fas fa-eye"></i>
              </button> -->
              <!-- 下載按鈕 -->
              <!-- <button type="button"
                      class="btn btn-success btn-sm"
                      *ngIf="file.downloadUrl && !file.error"
                      (click)="downloadFile(file)"
                      [title]="'file_upload.download_file' | translate">
                <i class="fas fa-download"></i>
              </button> -->
              <!-- 重新命名、刪除（預覽模式或允許用戶上傳且允許重命名時顯示） -->
              <!-- <button type="button"
                      class="btn btn-warning btn-sm"
                      *ngIf="(preview || config.allowUserUpload) && config.allowRename && !file.error"
                      (click)="startRename(i)"
                      [title]="'file_upload.rename_file' | translate">
                <i class="fas fa-edit"></i>
              </button> -->
              <button type="button"
                      class="btn btn-danger btn-sm"
                      *ngIf="preview || config.allowUserUpload"
                      (click)="removeFile(i)"
                      [title]="'file_upload.delete_file' | translate">
                <i class="fas fa-trash"></i>
              </button>
            </div>

            <!-- 預覽 -->
            <div class="file-preview" *ngIf="config.enablePreview && file.previewUrl && !file.error">
              <img *ngIf="isImageFile(file.type)"
                   [src]="file.previewUrl"
                   [alt]="file.name"
                   class="preview-image"
                   (error)="onPreviewError($event, file)">
            </div>
          </div>
        </div>
      </div>

      <!-- 重新命名對話框（預覽模式或允許用戶上傳時顯示） -->
      <div class="rename-dialog" *ngIf="(preview || config.allowUserUpload) && renamingIndex !== -1">
        <div class="dialog-overlay" (click)="cancelRename()"></div>
        <div class="dialog-content">
          <h4>{{ 'file_upload.rename_file' | translate }}</h4>
          <input type="text"
                 [(ngModel)]="newFileName"
                 class="form-control"
                 (keyup.enter)="confirmRename()"
                 (keyup.escape)="cancelRename()">
          <div class="dialog-actions">
            <button type="button" class="btn btn-secondary" (click)="cancelRename()">
              {{ 'common.cancel' | translate }}
            </button>
            <button type="button" class="btn btn-primary" (click)="confirmRename()">
              {{ 'common.confirm' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadManagerComponent),
      multi: true
    }
  ],
  styleUrls: ['./file-upload-manager.component.scss']
})
export class FileUploadManagerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() config: FileUploadConfig = {};
  @Input() preview: boolean = true;
  @Input() files: FileItem[] = [];
  @Input() exampleFiles: FileItem[] = []; // 範例檔案

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  isDragOver = false;
  renamingIndex = -1;
  newFileName = '';

  // 分離的檔案陣列
  userUploadedFiles: FileItem[] = []; // 使用者上傳的檔案

  private subscriptions: Subscription[] = [];

  // ControlValueAccessor callbacks
  onChange = (value: FileUploadValue) => {};
  onTouched = () => {};

  constructor(
    private fileUploadService: FileUploadService,
    private guidService: GuidService,
    private i18nService: I18nService
  ) {}

  ngOnInit(): void {

    console.log("=== FileUploadManager 初始化 ===");
    console.log("preview", this.preview);
    console.log("config", this.config);
    console.log("初始檔案:", this.files);
    console.log("範例檔案:", this.exampleFiles);

    // 設定預設配置，但如果 this.config 已經有值，則保留原有設定
    this.config = {
      maxFiles: 10,
      maxFileSize: 10,
      allowedTypes: ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx'],
      enableDragDrop: true,
      enablePreview: true,
      allowRename: true,
      showFileSize: true,
      showUploadTime: true,
      uploadButtonText: 'file_upload.select_files',
      dragDropText: 'file_upload.drag_drop_text',
      allowUserUpload: true,
      ...this.config
    };

    console.log("合併後的 config:", this.config);

    // 處理範例檔案
    if (this.exampleFiles && this.exampleFiles.length > 0) {
      this.processExampleFiles();
    }

    // 處理傳入的初始檔案（從資料庫載入的檔案）
    if (this.files && this.files.length > 0) {
      this.processInitialFiles();
    } else {
      // 如果沒有初始檔案，仍需要分離檔案
      this.separateFiles();
    }

    console.log("=== 初始化完成 ===");
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // ControlValueAccessor implementation
  writeValue(value: FileUploadValue | FileItem[]): void {
    console.log('writeValue 接收到的值:', value);

    if (value) {
      if (Array.isArray(value)) {
        this.files = value;
      } else if (value.files) {
        this.files = value.files;
      }

      // 重新分離檔案
      this.userUploadedFiles = this.files.filter(file => file.isExample !== true);

      console.log('writeValue 設定後的檔案:');
      console.log('- 主檔案列表:', this.files);
      console.log('- 使用者檔案列表:', this.userUploadedFiles);
    } else {
      this.files = [];
      this.userUploadedFiles = [];
    }
  }

  registerOnChange(fn: (value: FileUploadValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // 檔案處理方法
  triggerFileSelect(): void {
    if (this.disabled || !this.canUploadMore() || !(this.preview || this.config.allowUserUpload)) return;
    this.fileInputRef.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    if (!(this.preview || this.config.allowUserUpload)) return;

    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(input.files);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled && this.config.enableDragDrop && (this.preview || this.config.allowUserUpload)) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.disabled || !this.config.enableDragDrop || !(this.preview || this.config.allowUserUpload)) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  private async handleFiles(fileList: FileList): Promise<void> {

    console.log("handleFiles", fileList);

    const filesToProcess = Array.from(fileList).slice(0, this.getRemainingSlots());

    for (const file of filesToProcess) {
      if (this.validateFile(file)) {
        const fileItem: FileItem = {
          id: this.guidService.generateGuid(),
          name: file.name,
          originalName: file.name,
          blobName: '',
          size: file.size,
          type: file.type || this.getFileTypeFromExtension(file.name),
          uploadedAt: new Date(),
          uploading: true,
          uploadProgress: 0,
          isExample: false // 明確標記為使用者上傳的檔案
        };

        this.files.push(fileItem);
        this.userUploadedFiles.push(fileItem);
        this.emitChange();

        // 開始上傳
        await this.uploadFile(file, fileItem);
      }
    }
  }

  private validateFile(file: File): boolean {
    // 檔案大小檢查
    if (this.config.maxFileSize && file.size > this.config.maxFileSize * 1024 * 1024) {
      alert(this.i18nService.translate('file_upload.file_size_exceeded', { maxSize: this.config.maxFileSize }));
      return false;
    }

    // 檔案類型檢查
    if (this.config.allowedTypes && this.config.allowedTypes.length > 0) {
      const fileExtension = this.getFileExtension(file.name);
      if (!this.config.allowedTypes.includes(fileExtension)) {
        alert(this.i18nService.translate('file_upload.unsupported_file_type', { fileType: fileExtension }));
        return false;
      }
    }

    return true;
  }

  private async uploadFile(file: File, fileItem: FileItem): Promise<void> {

    console.log("uploadFile", file, fileItem);

    const uploadSub = this.fileUploadService.uploadFile(file).subscribe({
      next: (event) => {


        if (event.type === 'progress') {
          fileItem.uploadProgress = event.progress;
          // 觸發變更檢測以更新進度條
          this.emitChange();
        } else if (event.type === 'success') {

          console.log('檔案上傳成功:', event.response.data);
          fileItem.uploading = false;
          fileItem.blobName = event.response.data.blobName;
          fileItem.uploadedAt = new Date();

          // 產生查看和下載連結
          this.generateFileUrls(fileItem);

          // 發出變更事件以更新UI
          this.emitChange();
        }
      },
      error: (error) => {
        fileItem.uploading = false;
        fileItem.error = this.i18nService.translate('file_upload.upload_failed', {
          error: error.message || this.i18nService.translate('file_upload.unknown_error')
        });
        this.emitChange();
      }
    });

    this.subscriptions.push(uploadSub);
  }

  private generateFileUrls(fileItem: FileItem): void {
    if (!fileItem.blobName) return;

    // 產生查看連結
    const viewSub = this.fileUploadService.generateViewUrl(fileItem.blobName).subscribe({
      next: (response) => {
        // 統一處理回應資料結構
        fileItem.viewUrl = response.url || response.data?.viewUrl;

        console.log('查看連結已生成:', fileItem.viewUrl);
        this.emitChange();
      },
      error: (error) => {
        console.error('產生查看連結失敗:', error);
      }
    });

    // 產生下載連結
    const downloadSub = this.fileUploadService.generateDownloadUrl(fileItem.blobName).subscribe({
      next: (response) => {
        // 統一處理回應資料結構
        fileItem.downloadUrl = response.url || response.data?.downloadUrl;

        if (this.config.enablePreview && this.isImageFile(fileItem.type)) {
          fileItem.previewUrl = fileItem.downloadUrl;
        }
        this.emitChange();
      },
      error: (error) => {
        console.error('產生下載連結失敗:', error);
      }
    });

    this.subscriptions.push(viewSub, downloadSub);
  }

  // 檔案操作方法
  viewFile(file: FileItem): void {
    if (file.viewUrl) {
      window.open(file.viewUrl, '_blank');
    } else if (file.blobName) {
      // 動態生成查看連結
      const viewSub = this.fileUploadService.generateViewUrl(file.blobName).subscribe({
        next: (response) => {
          // 統一處理回應資料結構
          file.viewUrl = response.url || response.data?.viewUrl;
          if (file.viewUrl) {
            window.open(file.viewUrl, '_blank');
          }
          // 不需要在查看時觸發 emitChange，因為這只是更新 URL
        },
        error: (error) => {
          console.error('產生查看連結失敗:', error);
          alert(this.i18nService.translate('file_upload.cannot_open_file'));
        }
      });
      this.subscriptions.push(viewSub);
    } else {
      alert(this.i18nService.translate('file_upload.file_link_unavailable'));
    }
  }

  downloadFile(file: FileItem): void {
    if (file.downloadUrl) {
      const link = document.createElement('a');
      link.href = file.downloadUrl;
      link.download = file.name;
      link.click();
    } else if (file.blobName) {
      // 動態生成下載連結
      const downloadSub = this.fileUploadService.generateDownloadUrl(file.blobName).subscribe({
        next: (response) => {
          file.downloadUrl = response.url;
          if (file.downloadUrl) {
            const link = document.createElement('a');
            link.href = file.downloadUrl;
            link.download = file.name;
            link.click();
          }
          this.emitChange();
        },
        error: (error) => {
          console.error('產生下載連結失敗:', error);
          alert(this.i18nService.translate('file_upload.cannot_download_file'));
        }
      });
      this.subscriptions.push(downloadSub);
    } else {
      alert(this.i18nService.translate('file_upload.download_link_unavailable'));
    }
  }

  removeFile(index: number): void {
    const file = this.userUploadedFiles[index];

    if (file.blobName && !file.uploading) {
      // 從伺服器刪除檔案
      const deleteSub = this.fileUploadService.deleteFile(file.blobName).subscribe({
        next: () => {
          console.log('檔案已從伺服器刪除:', file.name);
        },
        error: (error) => {
          console.error('刪除檔案失敗:', error);
        }
      });

      this.subscriptions.push(deleteSub);
    }

    // 從使用者上傳的檔案列表中移除
    this.userUploadedFiles.splice(index, 1);

    // 同時從主檔案列表中移除
    const mainIndex = this.files.findIndex(f => f.id === file.id);
    if (mainIndex > -1) {
      this.files.splice(mainIndex, 1);
    }

    this.emitChange();
  }

  startRename(index: number): void {
    this.renamingIndex = index;
    this.newFileName = this.userUploadedFiles[index].name;
  }

  confirmRename(): void {
    if (this.renamingIndex >= 0 && this.newFileName.trim()) {
      this.userUploadedFiles[this.renamingIndex].name = this.newFileName.trim();

      // 同時更新主檔案列表中的名稱
      const fileId = this.userUploadedFiles[this.renamingIndex].id;
      const mainFile = this.files.find(f => f.id === fileId);
      if (mainFile) {
        mainFile.name = this.newFileName.trim();
      }

      this.emitChange();
    }
    this.cancelRename();
  }

  cancelRename(): void {
    this.renamingIndex = -1;
    this.newFileName = '';
  }

  // 工具方法
  canUploadMore(): boolean {
    // 只計算使用者上傳的檔案，不包含範例檔案
    const userUploadedCount = this.userUploadedFiles.length;
    return !this.config.maxFiles || userUploadedCount < this.config.maxFiles;
  }

  getRemainingSlots(): number {
    if (!this.config.maxFiles) return 100;
    const userUploadedCount = this.userUploadedFiles.length;
    return Math.max(0, this.config.maxFiles - userUploadedCount);
  }

  getAcceptedTypes(): string {
    return this.config.allowedTypes ? this.config.allowedTypes.join(',') : '*';
  }

  getUploadHint(): string {
    const hints = [];
    if (this.config.allowedTypes) {
      hints.push(`${this.i18nService.translate('file_upload.supported_formats')}: ${this.config.allowedTypes.join(', ')}`);
    }
    if (this.config.maxFileSize) {
      hints.push(`${this.i18nService.translate('file_upload.max_size')} ${this.config.maxFileSize}MB`);
    }
    if (this.config.maxFiles) {
      hints.push(`${this.i18nService.translate('file_upload.max_files')} ${this.config.maxFiles} ${this.i18nService.translate('file_upload.files_unit')}`);
    }
    return hints.join('，');
  }

  getFileExtension(fileName: string): string {
    return '.' + fileName.split('.').pop()?.toLowerCase() || '';
  }

  getFileTypeFromExtension(fileName: string): string {
    const ext = this.getFileExtension(fileName);
    const typeMap: { [key: string]: string } = {
      '.pdf': 'application/pdf',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.gif': 'image/gif',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    return typeMap[ext] || 'application/octet-stream';
  }

  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) {
      return 'fas fa-file-image text-success';
    } else if (fileType === 'application/pdf') {
      return 'fas fa-file-pdf text-danger';
    } else if (fileType.includes('word')) {
      return 'fas fa-file-word text-primary';
    }
    return 'fas fa-file text-secondary';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatUploadTime(date: Date): string {
    return date.toLocaleString();
  }

  isImageFile(fileType: string): boolean {
    return fileType.startsWith('image/');
  }

  onPreviewError(event: Event, file: FileItem): void {
    console.error('預覽載入失敗:', file.name);
    file.previewUrl = undefined;
  }

  private emitChange(): void {
    const value: FileUploadValue = {
      files: this.userUploadedFiles, // 只傳送使用者上傳的檔案
      metadata: {
        totalFiles: this.userUploadedFiles.length,
        totalSize: this.userUploadedFiles.reduce((sum, file) => sum + file.size, 0),
        lastUpdated: new Date()
      }
    };

    this.onChange(value);
    this.onTouched();
  }

  private separateFiles(): void {
    // 分離範例檔案和使用者上傳的檔案
    // 沒有明確標記為範例檔案的，都視為使用者上傳的檔案
    this.userUploadedFiles = this.files.filter(file => file.isExample !== true);

    console.log('原始檔案:', this.files);
    console.log('分離後的使用者檔案:', this.userUploadedFiles);
  }

  private processExampleFiles(): void {
    // 處理範例檔案，確保它們有必要的屬性
    this.exampleFiles.forEach(file => {
      file.isExample = true;

      if (!file.uploading) {
        file.uploading = false;
      }
      if (!file.uploadProgress) {
        file.uploadProgress = 100;
      }

      // 為範例檔案生成 URL（如果有 blobName）
      if (file.blobName && !file.viewUrl && !file.downloadUrl) {
        this.generateFileUrls(file);
      }
    });

    console.log('處理後的範例檔案:', this.exampleFiles);
  }

  private processInitialFiles(): void {
    console.log('處理初始檔案:', this.files);

    // 從表單建置階段傳入的檔案應該被視為範例檔案
    this.files.forEach(file => {
      // 標記為範例檔案（從表單建置階段來的檔案）
      if (file.isExample === undefined) {
        file.isExample = true;
      }

      // 確保檔案有必要的屬性
      if (file.uploading === undefined) {
        file.uploading = false;
      }
      if (file.uploadProgress === undefined) {
        file.uploadProgress = 100;
      }

      // 從資料庫載入的檔案通常已經有 viewUrl 和 downloadUrl
      // 只有在沒有 URL 但有 blobName 的情況下才生成新的 URL
      if (file.blobName && (!file.viewUrl || !file.downloadUrl)) {
        this.generateFileUrls(file);
      }
    });

    // 將這些檔案加入到範例檔案列表中
    this.exampleFiles = [...this.exampleFiles, ...this.files.filter(file => file.isExample === true)];

    // 確保使用者檔案列表只包含非範例檔案
    this.userUploadedFiles = this.files.filter(file => file.isExample !== true);

    console.log('處理後的檔案狀態:');
    console.log('- 主檔案列表:', this.files);
    console.log('- 範例檔案列表:', this.exampleFiles);
    console.log('- 使用者檔案列表:', this.userUploadedFiles);

    // 注意：這裡不要呼叫 emitChange()，避免在初始化時觸發不必要的變更事件
  }
}
